/**
 * Hook: Admin-Thread (eine ausgewählte Konversation)
 *
 * Wird im Messenger-Layout für die rechts dargestellte Thread-Spalte genutzt.
 * Lädt Messages, hört auf WS-Events der gewählten conversationId, sendet als Admin.
 *
 * Auto-Mark-Read beim Auswählen einer Konversation (Admin schaut hin →
 * Backend-Counter resetten + Badge aktualisiert sich).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchAdminMessages,
  sendAdminMessage,
  sendAdminMessageWithImages,
  markAdminConversationRead,
  type AdminConversationDetail,
  type AdminChatMessage
} from '../services/support/adminChatService';
import { useChatWebSocket } from './useChatWebSocket';
import type { MessageStatus, UIMessage } from './useChatConversation';
import { compressImage } from '../services/support/imageCompression';

interface UseAdminChatThreadOptions {
  conversationId: string | null;
  enabled?: boolean;
}

interface UseAdminChatThreadResult {
  conversation: AdminConversationDetail | null;
  messages: UIMessage[];
  isLoading: boolean;
  error: Error | null;
  send: (
    content: string,
    options?: {
      isInternalNote?: boolean;
      images?: File[];
      captions?: (string | null)[];
      replyToMessageId?: string | null;
    }
  ) => Promise<void>;
  retry: (clientId: string) => Promise<void>;
}

function toUIMessage(m: AdminChatMessage): UIMessage {
  return {
    ...m,
    status: m.readAt ? 'read' : m.deliveredAt ? 'delivered' : 'sent'
  };
}

export function useAdminChatThread(options: UseAdminChatThreadOptions): UseAdminChatThreadResult {
  const enabled = options.enabled !== false;
  const conversationId = options.conversationId;

  const [conversation, setConversation] = useState<AdminConversationDetail | null>(null);
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const conversationIdRef = useRef<string | null>(null);
  // Aktueller messages-Snapshot in einer Ref — der send()-useCallback hat
  // [] als Deps; Reply-Snippet muss aber aus dem aktuellen State gelesen werden.
  const messagesRef = useRef<UIMessage[]>([]);

  const { subscribe, sendClientEvent } = useChatWebSocket({ enabled });

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Initial-Load + Auto-Mark-Read bei Konversations-Wechsel
  useEffect(() => {
    conversationIdRef.current = conversationId;

    if (!enabled || !conversationId) {
      setConversation(null);
      setMessages([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const { messages: loaded, conversation: conv } = await fetchAdminMessages(conversationId);
        if (cancelled) return;
        setConversation(conv);
        setMessages(loaded.map(toUIMessage));

        // Bulk-mark-delivered für User-Nachrichten ohne delivered_at.
        // Anders als mark-read passiert das auch im Hintergrund-Tab — der
        // Empfang ist ein technisches Faktum (Frame ist im Browser-Speicher),
        // unabhängig davon ob der Admin gerade hinschaut.
        const hasUndeliveredUserMsgs = loaded.some(
          (m) => m.senderType === 'user' && !m.deliveredAt
        );
        if (hasUndeliveredUserMsgs) {
          sendClientEvent({ type: 'mark_delivered', conversationId });
        }

        // Auto-Mark-Read NUR wenn Admin-Tab tatsächlich sichtbar ist.
        // Sonst würde der User ✓✓-gelesen sehen, obwohl der Admin den Tab
        // im Hintergrund hat und gar nicht hinschaut. WhatsApp-Verhalten:
        // delivered (✓✓ grau) ist ok bei Hintergrund-Tab, read (✓✓ gold)
        // erst wenn der Admin wirklich hinguckt.
        if (conv.unreadCountAdmin > 0 && document.visibilityState === 'visible') {
          await markAdminConversationRead(conversationId);
          if (cancelled) return;
          setConversation((prev) => (prev ? { ...prev, unreadCountAdmin: 0 } : prev));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Konversation konnte nicht geladen werden'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, conversationId]);

  // WS: neue Nachricht in der aktuellen Konversation
  useEffect(() => {
    if (!enabled) return;
    return subscribe('message:new', (event) => {
      if (event.conversationId !== conversationIdRef.current) return;

      setMessages((prev) => {
        // Server-ID bereits im State → ignorieren (REST war schneller als WS-Echo)
        if (prev.some((m) => m.id === event.message.id)) return prev;

        // Race-Condition: WS-Echo kann VOR der REST-Antwort kommen. In dem Fall
        // ist im State noch eine optimistic-Message (id 'admin-temp-...') mit
        // gleichem Inhalt + senderType. Statt eine zweite Bubble hinzuzufügen,
        // ersetzen wir die Optimistic. Wenn die REST-Antwort später eintrifft,
        // findet sie die clientId aber NICHT mehr (id wurde bereits ersetzt) —
        // siehe send() unten, das ist ok.
        if (event.message.senderType === 'admin') {
          const matchIdx = prev.findIndex(
            (m) =>
              m.clientId !== undefined &&
              m.id.startsWith('admin-temp-') &&
              m.senderType === 'admin' &&
              m.content === event.message.content
          );
          if (matchIdx !== -1) {
            const next = [...prev];
            next[matchIdx] = {
              ...next[matchIdx]!,
              id: event.message.id,
              status: 'sent',
              createdAt: event.message.createdAt
            };
            return next;
          }
        }

        const incoming: UIMessage = {
          id: event.message.id,
          conversationId: event.conversationId,
          senderType: event.message.senderType,
          senderId: null,
          content: event.message.content,
          contentType: event.message.contentType,
          isInternalNote: false,
          deliveredAt: null,
          readAt: null,
          editedAt: null,
          createdAt: event.message.createdAt,
          attachments: (event.message.attachments ?? []).map((a) => ({ ...a, caption: a.caption ?? null })),
          replyTo: event.message.replyTo ?? null,
          status: 'sent'
        };
        return [...prev, incoming];
      });

      // Bei eingehender User-Nachricht: sofort delivered-ACK an Server schicken
      // (für ✓✓-Häkchen beim User). Auch im Hintergrund-Tab — der Empfang
      // ist ein technisches Faktum.
      if (event.message.senderType === 'user' && conversationIdRef.current) {
        sendClientEvent({ type: 'mark_delivered', conversationId: conversationIdRef.current });
      }

      // Auto-Read NUR wenn Admin tatsächlich hinguckt (Tab sichtbar).
      // Sonst sieht der User ✓✓-gelesen obwohl der Admin im Hintergrund-Tab ist.
      if (
        event.message.senderType === 'user' &&
        conversationIdRef.current &&
        document.visibilityState === 'visible'
      ) {
        const id = conversationIdRef.current;
        // Lokalen Counter sofort optimistisch zurückziehen, damit der Header
        // im Mobile-Sheet nicht kurz "1 ungelesen" anzeigt während der
        // Server-Roundtrip läuft.
        setConversation((prev) => (prev ? { ...prev, unreadCountAdmin: 0 } : prev));
        void markAdminConversationRead(id).catch(() => {});
      }
    });
  }, [enabled, subscribe, sendClientEvent]);

  // Wenn der Admin den Tab nach längerem Hintergrund wieder fokussiert UND
  // die Konversation aktive Nachrichten ungelesen hat: jetzt als gelesen
  // markieren. Damit greift Read-Receipt erst beim wirklichen Hingucken.
  useEffect(() => {
    if (!enabled || !conversationId) return;
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (!conversationIdRef.current) return;
      void markAdminConversationRead(conversationIdRef.current).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled, conversationId]);

  // WS: message:delivered → eigene Admin-Nachrichten upgraden auf 'delivered'
  useEffect(() => {
    if (!enabled) return;
    return subscribe('message:delivered', (event) => {
      if (event.conversationId !== conversationIdRef.current) return;
      // Aus Admin-Sicht interessant: User hat empfangen → deliveredByType='user'
      if (event.deliveredByType !== 'user') return;
      const ids = new Set(event.messageIds);
      setMessages((prev) =>
        prev.map((m) => {
          if (m.senderType !== 'admin') return m;
          if (!ids.has(m.id)) return m;
          if (m.status === 'read') return m;
          return { ...m, status: 'delivered', deliveredAt: event.deliveredAt };
        })
      );
    });
  }, [enabled, subscribe]);

  // WS: Read-Receipt vom User → eigene Admin-Nachrichten auf 'read'
  useEffect(() => {
    if (!enabled) return;
    return subscribe('message:read', (event) => {
      if (event.conversationId !== conversationIdRef.current) return;
      if (event.readByType !== 'user') return; // nur User-Reads interessant für Admin-UI
      setMessages((prev) =>
        prev.map((m) =>
          m.senderType === 'admin' &&
          (m.status === 'sent' || m.status === 'sending' || m.status === 'delivered')
            ? { ...m, status: 'read', readAt: event.readAt }
            : m
        )
      );
    });
  }, [enabled, subscribe]);

  const send = useCallback(
    async (
      content: string,
      opts: {
        isInternalNote?: boolean;
        images?: File[];
        captions?: (string | null)[];
        replyToMessageId?: string | null;
      } = {}
    ): Promise<void> => {
      const trimmed = content.trim();
      const images = opts.images ?? [];
      const captions = opts.captions ?? [];
      const hasImages = images.length > 0;
      if (!trimmed && !hasImages) return;
      const id = conversationIdRef.current;
      if (!id) return;

      if (images.length > 3) {
        throw new Error('Maximal 3 Bilder pro Nachricht.');
      }

      const clientId = `admin-temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const optimisticAttachments = images.map((file, i) => ({
        id: `${clientId}-att-${i}`,
        storagePath: URL.createObjectURL(file),
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        width: null,
        height: null,
        caption: (captions[i] ?? '').trim() || null
      }));

      // Optimistic Reply-Snippet aus aktuellem State
      let optimisticReplyTo: UIMessage['replyTo'] = null;
      if (opts.replyToMessageId) {
        const target = messagesRef.current.find((m) => m.id === opts.replyToMessageId);
        if (target) {
          const previewSrc = target.content.trim();
          optimisticReplyTo = {
            id: target.id,
            senderType: target.senderType,
            contentPreview: previewSrc.length > 80 ? previewSrc.slice(0, 79) + '…' : previewSrc,
            hasAttachments: target.attachments.length > 0
          };
        }
      }

      const optimistic: UIMessage = {
        id: clientId,
        clientId,
        conversationId: id,
        senderType: 'admin',
        senderId: null,
        content: trimmed,
        contentType: hasImages ? 'image' : 'text',
        isInternalNote: opts.isInternalNote === true,
        deliveredAt: null,
        readAt: null,
        editedAt: null,
        createdAt: new Date().toISOString(),
        attachments: optimisticAttachments,
        replyTo: optimisticReplyTo,
        status: 'sending'
      };

      setMessages((prev) => [...prev, optimistic]);

      try {
        let serverMsg: AdminChatMessage;
        if (hasImages) {
          const compressed = await Promise.all(images.map((f) => compressImage(f)));
          serverMsg = await sendAdminMessageWithImages(
            id,
            trimmed,
            compressed.map((c) => c.file),
            {
              ...(opts.isInternalNote !== undefined ? { isInternalNote: opts.isInternalNote } : {}),
              captions: captions.map((c) => (c ?? '').trim() || null),
              ...(opts.replyToMessageId ? { replyToMessageId: opts.replyToMessageId } : {})
            }
          );
        } else {
          serverMsg = await sendAdminMessage(id, trimmed, opts);
        }

        for (const att of optimisticAttachments) {
          try { URL.revokeObjectURL(att.storagePath); } catch { /* ignore */ }
        }

        setMessages((prev) =>
          prev.map((m) =>
            m.clientId === clientId
              ? { ...serverMsg, clientId, status: 'sent' as MessageStatus }
              : m
          )
        );
      } catch (err) {
        console.error('[Admin Chat] sendMessage failed', err);
        setMessages((prev) =>
          prev.map((m) => (m.clientId === clientId ? { ...m, status: 'failed' as MessageStatus } : m))
        );
      }
    },
    []
  );

  const retry = useCallback(
    async (clientId: string): Promise<void> => {
      const target = messages.find((m) => m.clientId === clientId);
      if (!target || target.status !== 'failed') return;
      setMessages((prev) => prev.filter((m) => m.clientId !== clientId));
      await send(target.content, { isInternalNote: target.isInternalNote });
    },
    [messages, send]
  );

  return { conversation, messages, isLoading, error, send, retry };
}
